import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { ArrowLeft, Send, CheckCircle2, AlertCircle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL ? `${BACKEND_URL}/api` : null;

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0 },
};

export default function ApplyPage() {
  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    age: "",
    project_name: "",
    project_description: "",
    category: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const newErrors = {};
    if (!formData.full_name.trim()) newErrors.full_name = "Full name is required";
    if (!formData.email.trim()) newErrors.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = "Enter a valid email";
    if (!formData.age) newErrors.age = "Age is required";
    else if (parseInt(formData.age) < 12 || parseInt(formData.age) > 18)
      newErrors.age = "Applicants must be between 12 and 18 years old";
    if (!formData.project_name.trim()) newErrors.project_name = "Project name is required";
    if (!formData.project_description.trim())
      newErrors.project_description = "Project description is required";
    if (!formData.category) newErrors.category = "Please select a category";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    try {
      if (API) {
        // Backend mode: submit to FastAPI
        await axios.post(`${API}/applications`, {
          ...formData,
          age: parseInt(formData.age),
        });
        setSubmitted(true);
        toast.success("Application submitted successfully!");
      } else {
        // Netlify Forms mode: submit via fetch with proper encoding
        const formElement = e.target;
        const formDataEncoded = new FormData(formElement);
        
        const response = await fetch("/", {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: new URLSearchParams(formDataEncoded).toString(),
        });

        if (response.ok || response.status === 404) {
          // Netlify returns 404 on form submission (expected behavior in SPA)
          setSubmitted(true);
          toast.success("Application submitted successfully!");
        } else {
          throw new Error("Form submission failed");
        }
      }
    } catch (err) {
      const detail = err.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  if (submitted) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center py-16">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          transition={{ duration: 0.5 }}
          className="text-center max-w-md mx-auto px-4"
        >
          <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-8 h-8 text-green-600" />
          </div>
          <h1
            data-testid="submission-success-title"
            className="font-heading text-2xl md:text-3xl font-bold text-[#111827] mb-3"
          >
            Application Submitted
          </h1>
          <p className="text-[#4B5563] text-base leading-relaxed mb-6">
            Thank you for applying to the GlitchRealm Foundation. Our team will
            review your application and get back to you soon.
          </p>
          <Link to="/">
            <Button
              data-testid="back-to-home-btn"
              variant="outline"
              className="rounded-md border-[#E5E7EB]"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="bg-[#F9FAFB] min-h-screen py-12 md:py-20">
      <div className="max-w-2xl mx-auto px-4 sm:px-6">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          transition={{ duration: 0.5 }}
        >
          <Link
            to="/"
            data-testid="back-link"
            className="inline-flex items-center text-sm text-[#4B5563] hover:text-[#0056D2] transition-colors mb-6"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back to Home
          </Link>

          <div className="mb-8">
            <h1
              data-testid="apply-page-title"
              className="font-heading text-3xl md:text-4xl font-bold text-[#111827] tracking-tight"
            >
              Apply for Support
            </h1>
            <p className="mt-3 text-[#4B5563] text-base md:text-lg leading-relaxed">
              Complete the form below to apply for a domain grant, hosting
              support, or project guidance from the GlitchRealm Foundation.
            </p>
          </div>
        </motion.div>

        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          transition={{ duration: 0.5, delay: 0.15 }}
        >
          <Card className="border-[#E5E7EB] bg-white shadow-sm">
            <CardHeader>
              <CardTitle className="font-heading text-xl text-[#111827]">
                Application Form
              </CardTitle>
              <CardDescription className="text-[#4B5563]">
                All fields are required. Applications are reviewed within 5
                business days.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} data-testid="application-form" name="application" method="POST" data-netlify="true" netlify-honeypot="bot-field">
                <input type="hidden" name="form-name" value="application" />
                <input type="hidden" name="bot-field" />
                <div className="space-y-5">
                  {/* Full Name */}
                  <div className="space-y-2">
                    <Label htmlFor="full_name" className="text-[#111827]">
                      Full Name
                    </Label>
                    <Input
                      id="full_name"
                      name="full_name"
                      data-testid="input-full-name"
                      placeholder="Your full name"
                      value={formData.full_name}
                      onChange={(e) => handleChange("full_name", e.target.value)}
                      className={errors.full_name ? "border-red-400" : ""}
                    />
                    {errors.full_name && (
                      <p className="text-red-500 text-xs flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {errors.full_name}
                      </p>
                    )}
                  </div>

                  {/* Email */}
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-[#111827]">
                      Email Address
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      data-testid="input-email"
                      type="email"
                      placeholder="your@email.com"
                      value={formData.email}
                      onChange={(e) => handleChange("email", e.target.value)}
                      className={errors.email ? "border-red-400" : ""}
                    />
                    {errors.email && (
                      <p className="text-red-500 text-xs flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {errors.email}
                      </p>
                    )}
                  </div>

                  {/* Age */}
                  <div className="space-y-2">
                    <Label htmlFor="age" className="text-[#111827]">
                      Age
                    </Label>
                    <Input
                      id="age"
                      name="age"
                      data-testid="input-age"
                      type="number"
                      min="12"
                      max="18"
                      placeholder="Your age (12-18)"
                      value={formData.age}
                      onChange={(e) => handleChange("age", e.target.value)}
                      className={errors.age ? "border-red-400" : ""}
                    />
                    {errors.age && (
                      <p className="text-red-500 text-xs flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {errors.age}
                      </p>
                    )}
                  </div>

                  <Separator />

                  {/* Project Name */}
                  <div className="space-y-2">
                    <Label htmlFor="project_name" className="text-[#111827]">
                      Project Name
                    </Label>
                    <Input
                      id="project_name"
                      name="project_name"
                      data-testid="input-project-name"
                      placeholder="Name of your project"
                      value={formData.project_name}
                      onChange={(e) =>
                        handleChange("project_name", e.target.value)
                      }
                      className={errors.project_name ? "border-red-400" : ""}
                    />
                    {errors.project_name && (
                      <p className="text-red-500 text-xs flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {errors.project_name}
                      </p>
                    )}
                  </div>

                  {/* Project Description */}
                  <div className="space-y-2">
                    <Label
                      htmlFor="project_description"
                      className="text-[#111827]"
                    >
                      Project Description
                    </Label>
                    <Textarea
                      id="project_description"
                      name="project_description"
                      data-testid="input-project-description"
                      placeholder="Tell us about your project: what it does, what technologies you're using, and what you hope to achieve."
                      rows={4}
                      value={formData.project_description}
                      onChange={(e) =>
                        handleChange("project_description", e.target.value)
                      }
                      className={
                        errors.project_description ? "border-red-400" : ""
                      }
                    />
                    {errors.project_description && (
                      <p className="text-red-500 text-xs flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {errors.project_description}
                      </p>
                    )}
                  </div>

                  {/* Category */}
                  <div className="space-y-2">
                    <Label className="text-[#111827]">
                      Support Category
                    </Label>
                    <input type="hidden" name="category" value={formData.category} />
                    <Select
                      value={formData.category}
                      onValueChange={(val) => handleChange("category", val)}
                    >
                      <SelectTrigger
                        data-testid="select-category"
                        className={errors.category ? "border-red-400" : ""}
                      >
                        <SelectValue placeholder="Select what type of support you need" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="domain_grant">
                          Domain Grant
                        </SelectItem>
                        <SelectItem value="hosting_support">
                          Hosting Support
                        </SelectItem>
                        <SelectItem value="project_guidance">
                          Project Guidance
                        </SelectItem>
                        <SelectItem value="community_resources">
                          Community Resources
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    {errors.category && (
                      <p className="text-red-500 text-xs flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {errors.category}
                      </p>
                    )}
                  </div>

                  <Separator />

                  {/* Info notice */}
                  <div className="bg-[#EFF6FF] border border-[#0056D2]/10 rounded-lg p-4">
                    <p className="text-sm text-[#4B5563]">
                      By submitting this application, you confirm that you are
                      between 12 and 18 years old and that the information
                      provided is accurate. Applications are typically reviewed
                      within 5 business days.
                    </p>
                  </div>

                  {/* Submit */}
                  <Button
                    type="submit"
                    data-testid="submit-application-btn"
                    disabled={submitting}
                    className="w-full bg-[#0056D2] hover:bg-[#0044A5] text-white rounded-md h-12 text-base"
                  >
                    {submitting ? (
                      "Submitting..."
                    ) : (
                      <>
                        Submit Application
                        <Send className="ml-2 w-4 h-4" />
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>

          {/* Eligibility reminder */}
          <div className="mt-6 text-center">
            <p className="text-sm text-[#9CA3AF]">
              Not sure if you qualify?{" "}
              <Link
                to="/#eligibility"
                data-testid="eligibility-link"
                className="text-[#0056D2] hover:underline"
              >
                Check eligibility requirements
              </Link>
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
